import { useState } from "react";
import { motion } from "framer-motion";

const PETALS = [
  { left: "8%", delay: 0.1, dur: 4.2, size: 12 },
  { left: "22%", delay: 0.8, dur: 4.8, size: 9 },
  { left: "36%", delay: 0.35, dur: 4.4, size: 14 },
  { left: "50%", delay: 1.1, dur: 5.0, size: 8 },
  { left: "64%", delay: 0.55, dur: 4.3, size: 11 },
  { left: "78%", delay: 1.3, dur: 4.7, size: 9 },
  { left: "90%", delay: 0.25, dur: 4.5, size: 13 },
];

const SPARKS = [15, 60, 105, 150, 195, 240, 285, 330].map((deg) => ({
  x: Math.cos((deg * Math.PI) / 180) * 36,
  y: Math.sin((deg * Math.PI) / 180) * 36,
}));

export default function EnvelopeOverlay({ details, onOpened }) {
  const [opening, setOpening] = useState(false);

  const handleOpen = () => {
    if (opening) return;
    setOpening(true);
    setTimeout(onOpened, 1750);
  };

  return (
    <motion.div
      data-testid="envelope-overlay-container"
      className="overlay-solid fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-6"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
    >
      {opening &&
        PETALS.map((p, i) => (
          <span
            key={i}
            className="petal"
            style={{
              left: p.left,
              width: p.size,
              height: p.size * 1.3,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.dur}s`,
            }}
          />
        ))}

      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: opening ? 0 : 1, y: 0 }}
        transition={opening ? { duration: 0.3 } : { delay: 0.2, duration: 0.8 }}
        className="font-editorial mb-2 text-lg italic text-[#8C8578]"
      >
        {details.name1} & {details.name2}
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: opening ? 0 : 1 }}
        transition={opening ? { duration: 0.3 } : { delay: 0.4, duration: 0.8 }}
        className="eyebrow mb-12"
      >
        კონვერტი
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          animate={opening ? { y: 10, scale: 0.965 } : { y: [0, -7, 0] }}
          transition={
            opening
              ? { duration: 0.9, ease: [0.22, 1, 0.36, 1] }
              : { repeat: Infinity, duration: 3.2, ease: "easeInOut" }
          }
        >
          <div
            data-testid="envelope-interactive-card"
            className="envelope-scene cursor-pointer"
            onClick={handleOpen}
            role="button"
            aria-label="კონვერტის გახსნა"
          >
            <div className="env-back" />

            <motion.div
              className="env-card"
              animate={opening ? { y: "-92%" } : { y: "0%" }}
              transition={opening ? { delay: 0.85, duration: 0.95, ease: [0.16, 1, 0.3, 1] } : {}}
            >
              <span className="eyebrow !text-[9px]">მოსაწვევი</span>
              <span className="text-xl leading-snug text-[#1C1917]">
                {details.name1} <span className="font-script gold-text text-2xl">&amp;</span>{" "}
                {details.name2}
              </span>
              <span className="font-display text-[11px] uppercase tracking-[0.2em] text-[#B8860B]">
                {details.date}
              </span>
            </motion.div>

            <div className="env-pocket" />
            <svg className="env-seams" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <path
                d="M0.3 0.3 L50 52 L99.7 0.3 M0.3 99.7 L50 52 M99.7 99.7 L50 52"
                fill="none"
                stroke="#D9C9A3"
                strokeWidth="0.7"
                vectorEffect="non-scaling-stroke"
              />
              <rect
                x="0.5"
                y="0.5"
                width="99"
                height="99"
                rx="2.5"
                fill="none"
                stroke="#D9C9A3"
                strokeWidth="0.7"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            <motion.div
              className="env-flap"
              animate={opening ? { rotateX: 180, zIndex: 5 } : { rotateX: 0, zIndex: 40 }}
              transition={{
                rotateX: opening ? { delay: 0.32, type: "spring", stiffness: 90, damping: 13 } : {},
                zIndex: { delay: 0.75, duration: 0 },
              }}
            />

            <motion.button
              data-testid="envelope-wax-seal-button"
              className="wax-seal"
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
              animate={
                opening
                  ? { scale: [1, 1.14, 0], opacity: [1, 1, 0], y: -14, rotate: -18 }
                  : { scale: 1, opacity: 1 }
              }
              transition={{ duration: 0.45, ease: "easeIn" }}
              aria-label="ოქროს ბეჭედი"
            >
              <span>
                {details.name1?.[0]}&amp;{details.name2?.[0]}
              </span>
            </motion.button>

            {opening &&
              SPARKS.map((s, i) => (
                <motion.span
                  key={i}
                  className="seal-spark"
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{ x: s.x, y: s.y, scale: [0, 1, 0], opacity: [1, 1, 0] }}
                  transition={{ duration: 0.65, ease: "easeOut", delay: 0.06 }}
                  aria-hidden="true"
                />
              ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.p
        data-testid="envelope-open-instruction-text"
        className="mt-14 text-xs uppercase tracking-[0.28em] text-[#A1A1AA]"
        animate={opening ? { opacity: 0 } : { opacity: [0.45, 1, 0.45] }}
        transition={opening ? { duration: 0.25 } : { repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
      >
        დააჭირეთ კონვერტს გასახსნელად
      </motion.p>
    </motion.div>
  );
}
